import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { MessageSquareWarning, ExternalLink } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { fetchObjections, fetchObjectionExamples, fetchCallFilterOptions } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';

const UNFINISHED_BAR_COLOR = '#60a5fa';
const EXAMPLES_LIMIT = 10;

const tooltipWrapperStyle = { zIndex: 30 };

const ObjectionsTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{item.label}</div>
      <div>Звонков: {Number(item.calls ?? 0).toLocaleString('ru-RU')}</div>
      <div>Доля: {Number(item.share ?? 0).toFixed(1)}%</div>
    </div>
  );
};

const formatExampleDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU');
};

const ObjectionsChart = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [serviceArea, setServiceArea] = useState('all');
  const [objections, setObjections] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [examples, setExamples] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);

  const baseParams = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return null;
    }

    return {
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      call_type: callType === 'all' ? undefined : callType,
      service_area: serviceArea === 'all' ? undefined : serviceArea
    };
  }, [dateRange?.from, dateRange?.to, callType, serviceArea]);

  const loadObjections = useCallback(async (showSpinner = true) => {
    if (!baseParams || isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    isFetching.current = true;
    setError(null);
    try {
      const data = await fetchObjections(baseParams);
      setObjections(data ?? null);
    } catch {
      setError('Не удалось загрузить причины отказов и незавершенных действий.');
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [baseParams]);

  useEffect(() => {
    loadObjections();
  }, [loadObjections]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadObjections(false);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadObjections]);

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return undefined;
    }

    let cancelled = false;

    fetchCallFilterOptions({
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      call_type: callType === 'all' ? undefined : callType
    })
      .then((response) => {
        if (!cancelled) {
          setFilterOptions(response ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFilterOptions(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dateRange?.from, dateRange?.to, callType]);

  // При смене фильтров выбранная причина и примеры сбрасываются.
  useEffect(() => {
    setSelectedObjection(null);
    setExamples([]);
  }, [baseParams]);

  const chartData = useMemo(
    () =>
      (objections?.items ?? []).map((item) => ({
        objection: item.objection,
        label: item.label ?? item.objection,
        calls: Number(item.calls) || 0,
        share: Number(item.share) || 0,
        valueLabel: `${Number(item.calls ?? 0).toLocaleString('ru-RU')} (${Number(item.share ?? 0).toFixed(1)}%)`
      })),
    [objections]
  );

  const chartHeight = Math.max(240, chartData.length * 40 + 40);

  const handleBarClick = useCallback(async (entry) => {
    const item = entry?.payload ?? entry;
    if (!item?.objection || !baseParams) {
      return;
    }

    if (selectedObjection?.objection === item.objection) {
      setSelectedObjection(null);
      setExamples([]);
      return;
    }

    setSelectedObjection({ objection: item.objection, label: item.label });
    setIsLoadingExamples(true);
    try {
      const data = await fetchObjectionExamples({
        ...baseParams,
        objection: item.objection,
        limit: EXAMPLES_LIMIT
      });
      setExamples(Array.isArray(data) ? data : []);
    } catch {
      setExamples([]);
    } finally {
      setIsLoadingExamples(false);
    }
  }, [baseParams, selectedObjection?.objection]);

  const handleOpenInJournal = useCallback(() => {
    if (!selectedObjection) {
      return;
    }

    onOpenCallJournal?.({
      objection: selectedObjection.objection,
      service_area: serviceArea === 'all' ? undefined : serviceArea,
      funnel_stage: 'refusal'
    });
  }, [selectedObjection, serviceArea, onOpenCallJournal]);

  const renderTaxonomySelect = (label, value, onChange, options) => (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      {label}:
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-[170px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Все</SelectItem>
          {(options ?? []).map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquareWarning className="h-5 w-5" />
              Причины отказов и возражения
            </CardTitle>
            <CardDescription>
              Доля считается от всех звонков
              {typeof objections?.denominatorCalls === 'number'
                ? ` (${objections.denominatorCalls.toLocaleString('ru-RU')})`
                : ''}
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {renderTaxonomySelect('Услуга', serviceArea, setServiceArea, filterOptions?.serviceAreas)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
        {!isLoading && chartData.length === 0 && (
          <p className="text-sm text-muted-foreground">Нет данных о возражениях за выбранный период.</p>
        )}
        {chartData.length > 0 && (
          <div className="w-full min-w-0 rounded-md bg-muted/30 px-2 py-4" style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 8, right: 96, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={190}
                  tick={{ fontSize: 11 }}
                  interval={0}
                />
                <RechartsTooltip
                  wrapperStyle={tooltipWrapperStyle}
                  cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                  content={<ObjectionsTooltip />}
                />
                <Bar
                  dataKey="calls"
                  name="Незавершенные обращения"
                  fill={UNFINISHED_BAR_COLOR}
                  fillOpacity={selectedObjection ? 0.85 : 1}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={28}
                  cursor="pointer"
                  onClick={handleBarClick}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Нажмите на столбец, чтобы посмотреть примеры звонков с этой причиной. Повторный клик скрывает примеры.
        </p>

        {selectedObjection && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">
                Примеры звонков: {selectedObjection.label}
              </h4>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleOpenInJournal}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Открыть в журнале
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setSelectedObjection(null); setExamples([]); }}>
                  Скрыть
                </Button>
              </div>
            </div>
            {isLoadingExamples && <p className="text-sm text-muted-foreground">Загрузка примеров...</p>}
            {!isLoadingExamples && examples.length === 0 && (
              <p className="text-sm text-muted-foreground">Примеры не найдены.</p>
            )}
            {!isLoadingExamples && examples.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Дата</TableHead>
                      <TableHead>Причина(ы)</TableHead>
                      <TableHead>Итог звонка</TableHead>
                      <TableHead>Услуга</TableHead>
                      <TableHead>Оператор</TableHead>
                      <TableHead>Краткое содержание</TableHead>
                      <TableHead>Фрагмент исхода</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examples.map((example) => (
                      <TableRow key={example.callId}>
                        <TableCell className="whitespace-nowrap">{formatExampleDate(example.callStartedAt)}</TableCell>
                        <TableCell>
                          {(example.objectionLabels?.length ? example.objectionLabels : example.objections ?? []).join(', ') || '—'}
                        </TableCell>
                        <TableCell>{example.outcome ?? example.bookingOutcomeLabel ?? example.bookingOutcome ?? '—'}</TableCell>
                        <TableCell>{example.serviceAreaLabel ?? example.serviceArea ?? '—'}</TableCell>
                        <TableCell>{example.employeeName ?? '—'}</TableCell>
                        <TableCell className="max-w-[320px]">
                          <span className="line-clamp-3">{example.summary ?? '—'}</span>
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          <span className="line-clamp-3 italic text-muted-foreground">
                            {example.bookingOutcomeEvidence ? `«${example.bookingOutcomeEvidence}»` : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onOpenCallJournal?.({ callId: example.callId })}
                          >
                            Открыть звонок
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ObjectionsChart;
